/* eslint-disable @typescript-eslint/no-explicit-any */
import { SupabaseAuthProvider, SupabaseSession, SupabaseUser } from '../types/supabase';
import { persistence } from 'services/persistence';

const STORAGE_KEY = 'supabase-session';

const supabaseUrl = import.meta.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.REACT_APP_SUPABASE_ANON_KEY;

if (supabaseUrl && supabaseUrl.endsWith('/')) {
  throw new Error('REACT_APP_SUPABASE_URL must not include a trailing slash');
}

const isConfigured = Boolean(supabaseUrl && supabaseAnonKey);

const getRedirectUrl = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  const url = new URL(window.location.href);
  url.hash = '';
  url.searchParams.set('auth', 'callback');
  return url.toString();
};

const persistSession = (session: SupabaseSession | null) => {
  if (!session) {
    persistence.removeItem(STORAGE_KEY);
    return;
  }
  persistence.setJSON(STORAGE_KEY, session);
};

const loadPersistedSession = (): SupabaseSession | null => {
  const parsed = persistence.getJSON<SupabaseSession | null>(STORAGE_KEY, null);
  if (!parsed?.accessToken || !parsed?.refreshToken || !parsed?.expiresAt) {
    return null;
  }
  return parsed;
};

const clearUrlFragment = () => {
  if (typeof window === 'undefined') {
    return;
  }
  if (window.location.hash.includes('access_token')) {
    const url = new URL(window.location.href);
    url.hash = '';
    window.history.replaceState(window.history.state, document.title, url.toString());
  }
};

const buildHeaders = (session?: SupabaseSession | null) => {
  if (!isConfigured) {
    throw new Error('Supabase is not configured');
  }
  const headers: Record<string, string> = {
    apikey: supabaseAnonKey as string,
    'Content-Type': 'application/json',
  };
  if (session?.accessToken) {
    headers.Authorization = `Bearer ${session.accessToken}`;
  }
  return headers;
};

const parseHashParams = (hash: string) => {
  return hash
    .replace(/^#/, '')
    .split('&')
    .map((segment) => segment.split('='))
    .reduce<Record<string, string>>((acc, [key, value]) => {
      if (!key) {
        return acc;
      }
      acc[decodeURIComponent(key)] = decodeURIComponent(value ?? '');
      return acc;
    }, {});
};

const fetchUser = async (session: SupabaseSession): Promise<SupabaseUser> => {
  if (!isConfigured) {
    throw new Error('Supabase is not configured');
  }
  const response = await fetch(`${supabaseUrl}/auth/v1/user`, {
    method: 'GET',
    headers: buildHeaders(session),
  });
  if (!response.ok) {
    throw new Error(`Failed to load user: ${response.status}`);
  }
  const data = (await response.json()) as { user: SupabaseUser } | SupabaseUser;
  if ('user' in data && data.user) {
    return data.user;
  }
  return data as SupabaseUser;
};

const createSession = async (params: {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}): Promise<SupabaseSession> => {
  const { accessToken, refreshToken, expiresIn } = params;
  const expiresAt = Math.floor(Date.now() / 1000) + expiresIn;
  const base: SupabaseSession = {
    accessToken,
    refreshToken,
    expiresAt,
    user: null,
  };
  const user = await fetchUser(base);
  return { ...base, user };
};

const refreshSession = async (session: SupabaseSession): Promise<SupabaseSession> => {
  if (!isConfigured) {
    throw new Error('Supabase is not configured');
  }
  const response = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=refresh_token`, {
    method: 'POST',
    headers: buildHeaders(session),
    body: JSON.stringify({
      refresh_token: session.refreshToken,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to refresh session (${response.status})`);
  }

  const data = await response.json();
  return createSession({
    accessToken: data.access_token,
    refreshToken: data.refresh_token ?? session.refreshToken,
    expiresIn: data.expires_in,
  });
};

export const supabaseAuth = {
  isConfigured,
  loadPersistedSession,
  persistSession,
  clearUrlFragment,
  async initializeFromHash(): Promise<SupabaseSession | null> {
    if (!isConfigured || typeof window === 'undefined') {
      return null;
    }

    if (!window.location.hash.includes('access_token')) {
      return null;
    }

    const params = parseHashParams(window.location.hash);
    if (!params.access_token || !params.refresh_token || !params.expires_in) {
      clearUrlFragment();
      return null;
    }

    const session = await createSession({
      accessToken: params.access_token,
      refreshToken: params.refresh_token,
      expiresIn: Number(params.expires_in),
    });
    persistSession(session);
    clearUrlFragment();
    return session;
  },

  async ensureSession(): Promise<SupabaseSession | null> {
    if (!isConfigured) {
      return null;
    }

    let session = loadPersistedSession();
    if (!session) {
      session = await this.initializeFromHash();
      return session;
    }

    const now = Math.floor(Date.now() / 1000);
    if (session.expiresAt - now < 60) {
      try {
        const refreshed = await refreshSession(session);
        persistSession(refreshed);
        return refreshed;
      } catch (error) {
        console.warn('[SupabaseAuth] Failed to refresh session', error);
        persistSession(null);
        return null;
      }
    }

    return session;
  },

  async signIn(email: string, password: string): Promise<SupabaseSession> {
    if (!isConfigured) {
      throw new Error('Supabase is not configured');
    }
    const response = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=password`, {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({
        email,
        password,
      }),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(errorBody?.error_description || errorBody?.error || 'Failed to sign in');
    }

    const data = await response.json();
    const session = await createSession({
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresIn: data.expires_in || 3600,
    });
    persistSession(session);
    return session;
  },

  async signUp(email: string, password: string): Promise<SupabaseSession> {
    if (!isConfigured) {
      throw new Error('Supabase is not configured');
    }
    const response = await fetch(`${supabaseUrl}/auth/v1/signup`, {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({
        email,
        password,
        data: {},
      }),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(errorBody?.error_description || errorBody?.error || 'Failed to sign up');
    }

    const data = await response.json();
    
    // Sign up might return user without session if email confirmation is required
    if (!data.access_token) {
      throw new Error('Account created but email confirmation may be required. Please check your email.');
    }
    
    const session = await createSession({
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresIn: data.expires_in || 3600,
    });
    persistSession(session);
    return session;
  },

  async sendMagicLink(email: string): Promise<void> {
    if (!isConfigured) {
      throw new Error('Supabase is not configured');
    }
    const response = await fetch(`${supabaseUrl}/auth/v1/magiclink`, {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({
        email,
        data: {},
        create_user: true,
        redirect_to: getRedirectUrl(),
      }),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(errorBody?.error_description || errorBody?.error || 'Failed to send magic link');
    }
  },

  startOAuth(provider: SupabaseAuthProvider): void {
    if (!isConfigured || typeof window === 'undefined') {
      throw new Error('Supabase is not configured');
    }

    const redirectUrl = getRedirectUrl();
    const url = new URL(`${supabaseUrl}/auth/v1/authorize`);
    url.searchParams.set('provider', provider);
    url.searchParams.set('redirect_to', redirectUrl);
    url.searchParams.set('scope', 'email');
    url.searchParams.set('response_type', 'token');
    url.searchParams.set('flow_type', 'implicit');
    url.searchParams.set('access_type', 'offline');
    window.location.href = url.toString();
  },

  async signOut(session: SupabaseSession | null): Promise<void> {
    if (!isConfigured) {
      persistSession(null);
      return;
    }
    try {
      if (session?.accessToken) {
        await fetch(`${supabaseUrl}/auth/v1/logout`, {
          method: 'POST',
          headers: buildHeaders(session),
        });
      }
    } catch (error) {
      console.warn('[SupabaseAuth] Sign out failed', error);
    } finally {
      persistSession(null);
    }
  },

  async refresh(session: SupabaseSession | null): Promise<SupabaseSession | null> {
    if (!session) {
      return null;
    }
    try {
      const refreshed = await refreshSession(session);
      persistSession(refreshed);
      return refreshed;
    } catch (error) {
      console.warn('[SupabaseAuth] Refresh failed', error);
      persistSession(null);
      return null;
    }
  },

  async getUser(session: SupabaseSession | null): Promise<SupabaseUser | null> {
    if (!session) {
      return null;
    }
    try {
      const user = await fetchUser(session);
      const merged = { ...session, user };
      persistSession(merged);
      return user;
    } catch (error) {
      console.warn('[SupabaseAuth] Failed to fetch user', error);
      return session.user;
    }
  },
};

export type SupabaseAuthService = typeof supabaseAuth;

